"use client"

/**
 * Demo state — which edge case the prototype should show.
 *
 * The mock bank is deliberately full (~1.2K questions, every folder populated),
 * so the states that matter most in a young program (an empty bank, an import
 * that finds nothing or fails outright, an assessment with no blueprint) are
 * unreachable in a review. This picks one, and the surfaces read it instead of
 * faking data.
 *
 * Reviewers switch it from the profile menu. Persisted shell-global (it is a
 * property of the demo, not of a product) via `usePersistedState`.
 */

import * as React from "react"
import { usePersistedState } from "@exxatdesignux/ui/lib/persisted-state"

export const DEMO_STATE_KEY = "shell:demo-state:v1"

export type DemoStateId =
  | "populated"
  | "empty-bank"
  | "import-empty"
  | "import-fails"
  | "no-blueprint"

export interface DemoStateOption {
  id: DemoStateId
  label: string
  /** What a reviewer will see — shown under the label in the menu. */
  hint: string
}

export const DEMO_STATE_OPTIONS: readonly DemoStateOption[] = [
  {
    id: "populated",
    label: "Populated bank",
    hint: "The full mock bank and blueprint",
  },
  {
    id: "empty-bank",
    label: "Empty bank",
    hint: "No questions to pick from yet",
  },
  {
    id: "import-empty",
    label: "Import finds nothing",
    hint: "Extraction returns no questions",
  },
  {
    id: "import-fails",
    label: "Import fails",
    hint: "The read stops partway and the document has to be retried",
  },
  {
    id: "no-blueprint",
    label: "No blueprint",
    hint: "Nothing to measure the mix against",
  },
]

export const DEFAULT_DEMO_STATE: DemoStateId = "populated"

function normalizeDemoState(value: unknown): DemoStateId {
  return DEMO_STATE_OPTIONS.some((option) => option.id === value)
    ? (value as DemoStateId)
    : DEFAULT_DEMO_STATE
}

interface DemoStateContextValue {
  demoState: DemoStateId
  setDemoState: (next: DemoStateId) => void
}

const DemoStateContext = React.createContext<DemoStateContextValue>({
  demoState: DEFAULT_DEMO_STATE,
  setDemoState: () => {},
})

export function useDemoState() {
  return React.useContext(DemoStateContext)
}

export function DemoStateProvider({ children }: { children: React.ReactNode }) {
  const [stored, setStored] = usePersistedState<DemoStateId>(
    DEMO_STATE_KEY,
    DEFAULT_DEMO_STATE,
    { debounceMs: 0 },
  )

  const demoState = normalizeDemoState(stored)

  const setDemoState = React.useCallback(
    (next: DemoStateId) => setStored(next),
    [setStored],
  )

  const value = React.useMemo(
    () => ({ demoState, setDemoState }),
    [demoState, setDemoState],
  )

  return (
    <DemoStateContext.Provider value={value}>
      {children}
    </DemoStateContext.Provider>
  )
}
